import { useState, useEffect, useCallback } from "react";
import {
  ChevronDown,
  ChevronRight,
  Plus,
  Pencil,
  Trash2,
  X,
  Check,
  Loader2,
  Building2,
  Palette,
  Mail,
} from "lucide-react";
import { get, post, put, del } from "../api/client";
import { Partner, Tab } from "../types";

interface EditingState {
  type: "tab" | "partner";
  id: number;
  value: string;
}

const DEFAULT_PRIMARY = "#65ADDD";
const DEFAULT_ACCENT = "#1B2A4A";

function isValidHex(val: string): boolean {
  return val === "" || /^#[0-9A-Fa-f]{6}$/.test(val);
}

const DAY_LABELS: Record<number, string> = {
  1: "Maandag",
  2: "Dinsdag",
  3: "Woensdag",
  4: "Donderdag",
  5: "Vrijdag",
  6: "Zaterdag",
  0: "Zondag",
};

function ReviewMailSection({ partner, onSaved }: { partner: Partner; onSaved: (updated: Partner) => void }) {
  const [enabled, setEnabled] = useState(partner.review_mail_enabled ?? false);
  const [day, setDay] = useState(partner.review_mail_day ?? 1);
  const [hour, setHour] = useState(partner.review_mail_hour ?? 8);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

  async function handleSave() {
    setSaving(true);
    setError("");
    setSaved(false);
    try {
      const updated = await put<Partner>(`/partners/${partner.id}`, {
        review_mail_enabled: enabled,
        review_mail_day: day,
        review_mail_hour: hour,
      });
      setSaved(true);
      onSaved(updated);
      setTimeout(() => setSaved(false), 3000);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Opslaan mislukt.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="border-t border-slate-200 mt-4 pt-4">
      <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
        <Mail size={16} />
        Periodieke review-mail
      </h4>
      <div className="space-y-4">
        {/* Toggle */}
        <div className="flex items-center justify-between">
          <div>
            <span className="text-sm font-medium text-slate-700">Ingeschakeld</span>
            <p className="text-xs text-slate-400">Automatisch review-mails versturen op het ingestelde moment.</p>
          </div>
          <button
            type="button"
            onClick={() => setEnabled((v) => !v)}
            className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none ${
              enabled ? "bg-waves-blue" : "bg-slate-200"
            }`}
            role="switch"
            aria-checked={enabled}
          >
            <span
              className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ${
                enabled ? "translate-x-5" : "translate-x-0"
              }`}
            />
          </button>
        </div>

        {/* Day selector */}
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1.5">
            Dag van de week
          </label>
          <div className="relative">
            <select
              value={day}
              onChange={(e) => setDay(Number(e.target.value))}
              disabled={!enabled}
              className="input-field appearance-none pr-10 text-sm disabled:opacity-50"
            >
              {[1, 2, 3, 4, 5, 6, 0].map((d) => (
                <option key={d} value={d}>{DAY_LABELS[d]}</option>
              ))}
            </select>
            <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          </div>
        </div>

        {/* Hour selector */}
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1.5">
            Tijdstip
          </label>
          <div className="relative">
            <select
              value={hour}
              onChange={(e) => setHour(Number(e.target.value))}
              disabled={!enabled}
              className="input-field appearance-none pr-10 text-sm disabled:opacity-50"
            >
              {Array.from({ length: 24 }, (_, i) => (
                <option key={i} value={i}>{String(i).padStart(2, "0")}:00</option>
              ))}
            </select>
            <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          </div>
        </div>

        {enabled && (
          <p className="text-xs text-slate-500 bg-waves-blue-light/30 rounded-lg px-3 py-2">
            Review-mails worden elke {DAY_LABELS[day]} om {String(hour).padStart(2, "0")}:00 verstuurd.
          </p>
        )}

        {error && (
          <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
            {error}
          </p>
        )}

        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-waves-blue text-white hover:bg-waves-blue-hover transition-colors disabled:opacity-60 min-h-[44px]"
        >
          {saving ? (
            <><Loader2 size={14} className="animate-spin" />Opslaan…</>
          ) : saved ? (
            <><Check size={14} />Opgeslagen</>
          ) : (
            "Opslaan"
          )}
        </button>
      </div>
    </div>
  );
}

function BrandingSection({ partner, onSaved }: { partner: Partner; onSaved: (updated: Partner) => void }) {
  const [logoUrl, setLogoUrl] = useState(partner.logo_url ?? "");
  const [primaryColor, setPrimaryColor] = useState(partner.primary_color ?? DEFAULT_PRIMARY);
  const [accentColor, setAccentColor] = useState(partner.accent_color ?? DEFAULT_ACCENT);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

  async function handleSave() {
    if (!isValidHex(primaryColor)) {
      setError("Ongeldige primaire kleur (verwacht #RRGGBB).");
      return;
    }
    if (!isValidHex(accentColor)) {
      setError("Ongeldige accentkleur (verwacht #RRGGBB).");
      return;
    }
    setSaving(true);
    setError("");
    setSaved(false);
    try {
      const updated = await put<Partner>(`/partners/${partner.id}`, {
        logo_url: logoUrl.trim() || null,
        primary_color: primaryColor || null,
        accent_color: accentColor || null,
      });
      setSaved(true);
      onSaved(updated);
      setTimeout(() => setSaved(false), 3000);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Opslaan mislukt.");
    } finally {
      setSaving(false);
    }
  }

  const effectivePrimary = isValidHex(primaryColor) && primaryColor ? primaryColor : DEFAULT_PRIMARY;
  const effectiveAccent = isValidHex(accentColor) && accentColor ? accentColor : DEFAULT_ACCENT;

  return (
    <div className="border-t border-slate-200 mt-4 pt-4">
      <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
        <Palette size={16} />
        Branding
      </h4>
      <div className="space-y-3">
        {/* Logo URL */}
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1.5">
            Logo URL
          </label>
          <input
            type="text"
            value={logoUrl}
            onChange={(e) => setLogoUrl(e.target.value)}
            placeholder="https://example.com/logo.png"
            className="input-field text-sm w-full"
          />
          {logoUrl && (
            <img
              src={logoUrl}
              alt="Logo voorbeeld"
              className="h-8 mt-2 rounded object-contain"
              onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
              onLoad={(e) => { (e.target as HTMLImageElement).style.display = ""; }}
            />
          )}
        </div>

        {/* Color pickers */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1.5">
              Primaire kleur
            </label>
            <div className="flex gap-2">
              <input
                type="color"
                value={isValidHex(primaryColor) && primaryColor ? primaryColor : DEFAULT_PRIMARY}
                onChange={(e) => setPrimaryColor(e.target.value)}
                className="w-12 h-10 rounded cursor-pointer border border-slate-200 p-0.5"
                title="Kies primaire kleur"
              />
              <input
                type="text"
                value={primaryColor}
                onChange={(e) => setPrimaryColor(e.target.value)}
                placeholder="#65ADDD"
                maxLength={7}
                className={`input-field text-sm flex-1 font-mono ${
                  primaryColor && !isValidHex(primaryColor) ? "border-red-400 focus:ring-red-400" : ""
                }`}
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1.5">
              Accent kleur
            </label>
            <div className="flex gap-2">
              <input
                type="color"
                value={isValidHex(accentColor) && accentColor ? accentColor : DEFAULT_ACCENT}
                onChange={(e) => setAccentColor(e.target.value)}
                className="w-12 h-10 rounded cursor-pointer border border-slate-200 p-0.5"
                title="Kies accentkleur"
              />
              <input
                type="text"
                value={accentColor}
                onChange={(e) => setAccentColor(e.target.value)}
                placeholder="#1B2A4A"
                maxLength={7}
                className={`input-field text-sm flex-1 font-mono ${
                  accentColor && !isValidHex(accentColor) ? "border-red-400 focus:ring-red-400" : ""
                }`}
              />
            </div>
          </div>
        </div>

        {/* Preview */}
        <div
          className="rounded-lg p-4 flex items-center gap-3"
          style={{ backgroundColor: effectivePrimary }}
        >
          {logoUrl && (
            <img
              src={logoUrl}
              alt=""
              className="h-7 object-contain rounded"
              onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
              onLoad={(e) => { (e.target as HTMLImageElement).style.display = ""; }}
            />
          )}
          <div>
            <strong className="text-white text-sm">{partner.name}</strong>
            <span
              className="ml-2 text-xs px-2 py-0.5 rounded font-medium"
              style={{ backgroundColor: effectiveAccent, color: "white" }}
            >
              Voorbeeld
            </span>
          </div>
        </div>

        {error && (
          <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
            {error}
          </p>
        )}

        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-waves-blue text-white hover:bg-waves-blue-hover transition-colors disabled:opacity-60 min-h-[44px]"
        >
          {saving ? (
            <>
              <Loader2 size={14} className="animate-spin" />
              Opslaan…
            </>
          ) : saved ? (
            <>
              <Check size={14} />
              Opgeslagen
            </>
          ) : (
            "Opslaan"
          )}
        </button>
      </div>
    </div>
  );
}

interface AddingTabState {
  partnerId: number;
  name: string;
  location: string;
}

export default function TabConfig() {
  const [partners, setPartners] = useState<Partner[]>([]);
  const [tabs, setTabs] = useState<Tab[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [expandedPartners, setExpandedPartners] = useState<Set<number>>(new Set());
  const [editing, setEditing] = useState<EditingState | null>(null);
  const [addingTab, setAddingTab] = useState<AddingTabState | null>(null);
  const [showPartnerDialog, setShowPartnerDialog] = useState(false);
  const [newPartnerName, setNewPartnerName] = useState("");
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");

  const fetchData = useCallback(() => {
    setLoading(true);
    Promise.all([get<Partner[]>("/partners"), get<Tab[]>("/tabs")])
      .then(([p, t]) => {
        setPartners(p);
        setTabs(t);
        setExpandedPartners(new Set(p.map((x) => x.id)));
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  function toggleExpanded(id: number) {
    setExpandedPartners((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function handleSaveTab(tab: Tab) {
    if (!editing || editing.value.trim() === "") return;
    setSaving(true);
    setFormError("");
    try {
      await put(`/tabs/${tab.id}`, { name: editing.value.trim() });
      setTabs((prev) =>
        prev.map((t) => (t.id === tab.id ? { ...t, name: editing.value.trim() } : t))
      );
      setEditing(null);
    } catch (err: unknown) {
      setFormError(err instanceof Error ? err.message : "Opslaan mislukt.");
    } finally {
      setSaving(false);
    }
  }

  async function handleDeleteTab(tab: Tab) {
    if (!confirm(`Tab "${tab.name}" verwijderen? Dit kan niet ongedaan worden gemaakt.`)) return;
    try {
      await del(`/tabs/${tab.id}`);
      setTabs((prev) => prev.filter((t) => t.id !== tab.id));
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : "Verwijderen mislukt.");
    }
  }

  async function handleAddTab() {
    if (!addingTab || !addingTab.name.trim()) {
      setFormError("Tabbladnaam is verplicht.");
      return;
    }
    setSaving(true);
    setFormError("");
    try {
      const newTab = await post<Tab>("/tabs", {
        partner_id: addingTab.partnerId,
        name: addingTab.name.trim(),
        location: addingTab.location.trim() || addingTab.name.trim(),
      });
      setTabs((prev) => [...prev, newTab]);
      setAddingTab(null);
    } catch (err: unknown) {
      setFormError(err instanceof Error ? err.message : "Toevoegen mislukt.");
    } finally {
      setSaving(false);
    }
  }

  async function handleDeletePartner(partner: Partner) {
    if (!confirm(`Partner "${partner.name}" verwijderen? Alle bijbehorende tabs worden ook verwijderd.`)) return;
    try {
      await del(`/partners/${partner.id}`);
      setPartners((prev) => prev.filter((p) => p.id !== partner.id));
      setTabs((prev) => prev.filter((t) => t.partner_id !== partner.id));
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : "Verwijderen mislukt.");
    }
  }

  async function handleAddPartner() {
    if (!newPartnerName.trim()) {
      setFormError("Partnernaam is verplicht.");
      return;
    }
    setSaving(true);
    setFormError("");
    try {
      const newPartner = await post<Partner>("/partners", { name: newPartnerName.trim() });
      setPartners((prev) => [...prev, newPartner]);
      setExpandedPartners((prev) => new Set([...prev, newPartner.id]));
      setShowPartnerDialog(false);
      setNewPartnerName("");
    } catch (err: unknown) {
      setFormError(err instanceof Error ? err.message : "Toevoegen mislukt.");
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="w-8 h-8 border-2 border-waves-blue border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-6 text-sm">
        <strong>Fout bij laden:</strong> {error}
      </div>
    );
  }

  return (
    <div className="max-w-3xl space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <p className="text-sm text-slate-500">
            Beheer partners en hun vestigingen (tabs) in het portaal.
          </p>
        </div>
        <button
          onClick={() => {
            setFormError("");
            setNewPartnerName("");
            setShowPartnerDialog(true);
          }}
          className="btn-primary flex items-center gap-2 min-h-[44px] sm:min-h-0 justify-center sm:justify-start"
        >
          <Plus size={16} />
          Partner Toevoegen
        </button>
      </div>

      {/* Partner sections */}
      {partners.length === 0 ? (
        <div className="card p-12 text-center text-slate-400">
          <Building2 size={40} className="mx-auto mb-3 opacity-40" />
          <p>Geen partners gevonden. Voeg een partner toe om te beginnen.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {partners.map((partner) => {
            const partnerTabs = tabs.filter((t) => t.partner_id === partner.id);
            const expanded = expandedPartners.has(partner.id);

            return (
              <div key={partner.id} className="card overflow-hidden">
                {/* Partner header */}
                <div
                  className="flex items-center gap-3 px-4 md:px-5 py-4 cursor-pointer select-none hover:bg-slate-50 transition-colors min-h-[56px]"
                  onClick={() => toggleExpanded(partner.id)}
                >
                  <span className="text-slate-400">
                    {expanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                  </span>
                  <div
                    className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: "#E0EFF8" }}
                  >
                    <Building2 size={16} style={{ color: "#65ADDD" }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <span className="font-semibold text-slate-800">{partner.name}</span>
                    <span className="ml-2 text-xs text-slate-400">
                      {partnerTabs.length} tab{partnerTabs.length !== 1 ? "s" : ""}
                    </span>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeletePartner(partner);
                    }}
                    className="p-2 rounded hover:bg-red-50 text-slate-300 hover:text-red-500 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
                    title="Partner verwijderen"
                  >
                    <Trash2 size={15} />
                  </button>
                </div>

                {/* Tabs list */}
                {expanded && (
                  <div className="border-t border-slate-100">
                    {partnerTabs.length === 0 && addingTab?.partnerId !== partner.id ? (
                      <div className="px-4 md:px-5 py-4 text-sm text-slate-400 italic">
                        Geen tabs. Voeg een vestiging toe.
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="bg-slate-50 border-b border-slate-100">
                              <th className="text-left py-2.5 px-4 md:px-5 text-xs font-semibold text-slate-500 uppercase tracking-wide">
                                Tabbladnaam
                              </th>
                              <th className="text-left py-2.5 px-4 md:px-5 text-xs font-semibold text-slate-500 uppercase tracking-wide hidden sm:table-cell">
                                Locatie
                              </th>
                              <th className="w-20 md:w-24 py-2.5 px-4 md:px-5 text-xs font-semibold text-slate-500 uppercase tracking-wide text-right">
                                Acties
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {partnerTabs.map((tab) => (
                              <tr key={tab.id} className="border-b border-slate-50 last:border-0 hover:bg-slate-50/50">
                                <td className="py-3 px-4 md:px-5">
                                  {editing?.type === "tab" && editing.id === tab.id ? (
                                    <div className="flex items-center gap-2">
                                      <input
                                        className="input-field py-1.5"
                                        value={editing.value}
                                        onChange={(e) =>
                                          setEditing((prev) =>
                                            prev ? { ...prev, value: e.target.value } : prev
                                          )
                                        }
                                        autoFocus
                                        onKeyDown={(e) => {
                                          if (e.key === "Enter") handleSaveTab(tab);
                                          if (e.key === "Escape") setEditing(null);
                                        }}
                                      />
                                      <button
                                        onClick={() => handleSaveTab(tab)}
                                        disabled={saving}
                                        className="p-2 rounded hover:bg-green-50 text-green-600 min-h-[44px] min-w-[44px] flex items-center justify-center"
                                      >
                                        {saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                                      </button>
                                      <button
                                        onClick={() => setEditing(null)}
                                        className="p-2 rounded hover:bg-slate-100 text-slate-400 min-h-[44px] min-w-[44px] flex items-center justify-center"
                                      >
                                        <X size={14} />
                                      </button>
                                    </div>
                                  ) : (
                                    <div>
                                      <span className="font-medium text-slate-700">{tab.name}</span>
                                      {/* Show location inline on mobile since location column is hidden */}
                                      {tab.location && (
                                        <span className="sm:hidden text-xs text-slate-400 block mt-0.5">{tab.location}</span>
                                      )}
                                    </div>
                                  )}
                                </td>
                                <td className="py-3 px-4 md:px-5 text-slate-500 hidden sm:table-cell">{tab.location}</td>
                                <td className="py-3 px-4 md:px-5 text-right">
                                  <div className="flex items-center justify-end gap-1">
                                    <button
                                      onClick={() =>
                                        setEditing({ type: "tab", id: tab.id, value: tab.name })
                                      }
                                      className="p-2 rounded hover:bg-waves-blue-light text-slate-400 hover:text-waves-blue transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
                                      title="Bewerken"
                                    >
                                      <Pencil size={14} />
                                    </button>
                                    <button
                                      onClick={() => handleDeleteTab(tab)}
                                      className="p-2 rounded hover:bg-red-50 text-slate-400 hover:text-red-500 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
                                      title="Verwijderen"
                                    >
                                      <Trash2 size={14} />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}

                            {/* Add tab inline row */}
                            {addingTab?.partnerId === partner.id && (
                              <tr className="border-b border-slate-50 bg-waves-blue-light/30">
                                <td className="py-3 px-4 md:px-5">
                                  <input
                                    className="input-field py-1.5"
                                    value={addingTab.name}
                                    onChange={(e) =>
                                      setAddingTab((prev) => prev ? { ...prev, name: e.target.value } : prev)
                                    }
                                    placeholder="Tabbladnaam"
                                    autoFocus
                                  />
                                </td>
                                <td className="py-3 px-4 md:px-5 hidden sm:table-cell">
                                  <input
                                    className="input-field py-1.5"
                                    value={addingTab.location}
                                    onChange={(e) =>
                                      setAddingTab((prev) => prev ? { ...prev, location: e.target.value } : prev)
                                    }
                                    placeholder="Locatienaam (optioneel)"
                                  />
                                </td>
                                <td className="py-3 px-4 md:px-5 text-right">
                                  <div className="flex items-center justify-end gap-1">
                                    <button
                                      onClick={handleAddTab}
                                      disabled={saving}
                                      className="p-2 rounded hover:bg-green-50 text-green-600 min-h-[44px] min-w-[44px] flex items-center justify-center"
                                    >
                                      {saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                                    </button>
                                    <button
                                      onClick={() => {
                                        setAddingTab(null);
                                        setFormError("");
                                      }}
                                      className="p-2 rounded hover:bg-slate-100 text-slate-400 min-h-[44px] min-w-[44px] flex items-center justify-center"
                                    >
                                      <X size={14} />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {/* Add tab footer */}
                    {addingTab?.partnerId !== partner.id && (
                      <div className="px-4 md:px-5 py-3 border-t border-slate-100">
                        <button
                          onClick={() => {
                            setFormError("");
                            setAddingTab({ partnerId: partner.id, name: "", location: "" });
                          }}
                          className="flex items-center gap-1.5 text-sm font-medium text-waves-blue hover:text-waves-blue-hover transition-colors min-h-[44px]"
                        >
                          <Plus size={14} />
                          Tab toevoegen
                        </button>
                      </div>
                    )}

                    {formError && (
                      <div className="px-4 md:px-5 py-2 text-xs text-red-600">{formError}</div>
                    )}

                    {/* Branding subsection */}
                    <div className="px-4 md:px-5 pb-4">
                      <BrandingSection
                        partner={partner}
                        onSaved={(updated) =>
                          setPartners((prev) =>
                            prev.map((p) => (p.id === updated.id ? updated : p))
                          )
                        }
                      />
                      <ReviewMailSection
                        partner={partner}
                        onSaved={(updated) =>
                          setPartners((prev) =>
                            prev.map((p) => (p.id === updated.id ? updated : p))
                          )
                        }
                      />
                    </div>

                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Add partner dialog — full width on mobile */}
      {showPartnerDialog && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
          <div
            className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm"
            onClick={() => setShowPartnerDialog(false)}
          />
          <div className="relative bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-md p-6">
            <h3 className="font-semibold text-slate-800 text-lg mb-4">Nieuwe Partner Toevoegen</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Partnernaam <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={newPartnerName}
                  onChange={(e) => setNewPartnerName(e.target.value)}
                  placeholder="Bijv. Heijmans"
                  className="input-field"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleAddPartner();
                    if (e.key === "Escape") setShowPartnerDialog(false);
                  }}
                />
              </div>
              {formError && (
                <p className="text-sm text-red-600">{formError}</p>
              )}
              <div className="flex flex-col sm:flex-row justify-end gap-3">
                <button
                  onClick={() => setShowPartnerDialog(false)}
                  className="btn-secondary min-h-[44px] sm:min-h-0"
                >
                  Annuleren
                </button>
                <button
                  onClick={handleAddPartner}
                  disabled={saving}
                  className="btn-primary flex items-center justify-center gap-2 min-h-[44px] sm:min-h-0"
                >
                  {saving ? (
                    <>
                      <Loader2 size={14} className="animate-spin" />
                      Opslaan…
                    </>
                  ) : (
                    "Partner Aanmaken"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
